import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './ModernCalendar.css';
import {
    faCalendarAlt, faClock, faPlus, faEdit, faTrash, faBell,
    faPlay, faPause, faStop, faUser, faMapMarkerAlt, faVideo,
    faFilter, faSearch, faCog, faChevronLeft, faChevronRight,
    faList, faTh, faCalendarWeek, faCalendarDay,
    faExclamationTriangle, faCheckCircle, faTimesCircle,
    faBell as faAlarmClock, faStopwatch, faTasks, faUsers, faSync
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import DashboardFooter from '../layouts/dashboard_footer';
import { buildApiUrl, useBaseUrl } from '../../utils/utils';
import localForage from 'localforage';
import EventModal from './components/EventModal';
import TimerModal from './components/TimerModal';
import CalendarContextMenu from './components/CalendarContextMenu';

/* ─── Types ─── */
interface CalendarEvent {
    id: string;
    title: string;
    description?: string;
    event_type: 'appointment' | 'reminder' | 'task' | 'meeting' | 'deadline' | 'holiday' | 'training' | 'maintenance' | 'collaboration_task';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'postponed';
    start_datetime: string;
    end_datetime: string;
    is_all_day: boolean;
    location?: string;
    meeting_link?: string;
    notes?: string;
    created_by_name?: string;
    assigned_to_name?: string;
    patient_name?: string;
    duration?: number;
    is_overdue?: boolean;
    is_upcoming?: boolean;
    participants?: EventParticipant[];
    reminders?: EventReminder[];
    collaboration_id?: number;
    collaboration_title?: string;
    task_charge?: number;
}

interface EventParticipant {
    id: string;
    user_name: string;
    user_email: string;
    role: 'organizer' | 'attendee' | 'optional' | 'resource';
    response_status: 'pending' | 'accepted' | 'declined' | 'tentative';
}

interface EventReminder {
    id: string;
    reminder_type: 'email' | 'sms' | 'push' | 'popup' | 'sound';
    reminder_time: string;
    is_sent: boolean;
    message?: string;
}

interface Timer {
    id: string;
    title: string;
    description?: string;
    timer_type: 'pomodoro' | 'break' | 'focus' | 'custom';
    duration_minutes: number;
    is_active: boolean;
    started_at?: string;
    paused_at?: string;
    completed_at?: string;
    elapsed_time?: number;
    remaining_time?: number;
    progress_percentage?: number;
    event_title?: string;
}

interface CalendarStats {
    total_events: number;
    today_events: number;
    upcoming_events: number;
    overdue_events: number;
    completed_events: number;
    cancelled_events: number;
    events_by_type: Record<string, number>;
    events_by_priority: Record<string, number>;
    events_by_status: Record<string, number>;
}

interface CollabTask {
    id: number;
    task: string;
    task_description: string;
    task_status: string;
    start_date: string;
    end_date: string;
    task_charge: number;
    collaboration_id: number;
    collaboration_title?: string;
    patient_name?: string;
}

/* ─── Palette ─── */
const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

const TYPE_PALETTE: Record<string, { dot: string; bg: string; text: string; label: string }> = {
    appointment:        { dot: ACCENT,    bg: ACCENT,    text: '#fff',    label: 'Appointment' },
    meeting:            { dot: '#14b8a6', bg: '#14b8a6', text: '#fff',    label: 'Meeting' },
    task:               { dot: '#d97706', bg: '#d97706', text: '#fff',    label: 'Task' },
    collaboration_task: { dot: '#3b82f6', bg: '#3b82f6', text: '#fff',    label: 'Collab Task' },
    reminder:           { dot: '#ef4444', bg: '#ef4444', text: '#fff',    label: 'Reminder' },
    deadline:           { dot: '#dc2626', bg: '#dc2626', text: '#fff',    label: 'Deadline' },
    holiday:            { dot: '#06b6d4', bg: '#06b6d4', text: '#fff',    label: 'Holiday' },
    training:           { dot: '#8b5cf6', bg: '#8b5cf6', text: '#fff',    label: 'Training' },
    maintenance:        { dot: '#64748b', bg: '#64748b', text: '#fff',    label: 'Maintenance' },
};

const PRIORITY_COLORS: Record<string, string> = { low: 'success', medium: 'warning', high: 'danger', urgent: 'danger' };

const ModernCalendar: React.FC = () => {
    /* ─── State ─── */
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState<'month' | 'week' | 'day' | 'agenda'>('month');
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [collabTasks, setCollabTasks] = useState<CollabTask[]>([]);
    const [timers, setTimers] = useState<Timer[]>([]);
    const [stats, setStats] = useState<CalendarStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showTimerModal, setShowTimerModal] = useState(false);
    const [contextMenu, setContextMenu] = useState<{ show: boolean; x: number; y: number; date?: Date }>({ show: false, x: 0, y: 0 });
    const [eventModalMode, setEventModalMode] = useState<'create' | 'edit' | 'view'>('create');
    const [timerModalMode, setTimerModalMode] = useState<'create' | 'edit' | 'view'>('create');
    const [filter, setFilter] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTimers, setActiveTimers] = useState<Set<string>>(new Set());
    const [sidebarMiniDate, setSidebarMiniDate] = useState(new Date());
    const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
    const [showDayPopup, setShowDayPopup] = useState(false);

    const baseUrl = useBaseUrl();

    /* ─── Auth ─── */
    const getAuthHeaders = useCallback(async () => {
        const clinicId = await localForage.getItem('clinic_id') as string;
        const staffId = await localForage.getItem('staff_id') as string;
        return { 'X-Clinic-ID': clinicId || '', 'X-Staff-ID': staffId || '', 'Content-Type': 'application/json' };
    }, []);

    /* ─── Load data ─── */
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const headers = await getAuthHeaders();

            const [eventsRes, timersRes, statsRes, collabRes] = await Promise.allSettled([
                fetch(`${baseUrl}calendar/api/v1/events/`, { headers }),
                fetch(`${baseUrl}calendar/api/v1/timers/`, { headers }),
                fetch(`${baseUrl}calendar/api/v1/analytics/stats/`, { headers }),
                fetch(buildApiUrl(baseUrl, 'collaborations/tasks/calendar/'), { headers }),
            ]);

            if (eventsRes.status === 'fulfilled' && eventsRes.value.ok) {
                setEvents(await eventsRes.value.json());
            }
            if (timersRes.status === 'fulfilled' && timersRes.value.ok) {
                setTimers(await timersRes.value.json());
            }
            if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
                setStats(await statsRes.value.json());
            }
            if (collabRes.status === 'fulfilled' && collabRes.value.ok) {
                const cData = await collabRes.value.json();
                const tasks = cData?.data || cData || [];
                setCollabTasks(Array.isArray(tasks) ? tasks : []);
            }
        } catch (error) {
            console.error('Error loading calendar data:', error);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, getAuthHeaders]);

    useEffect(() => { document.title = 'Calendar — Uzima'; loadData(); }, [loadData]);

    /* ─── Merge collaboration tasks into events ─── */
    const mergedEvents: CalendarEvent[] = [
        ...events,
        ...collabTasks.map(t => ({
            id: `collab-${t.id}`,
            title: t.task,
            description: t.task_description,
            event_type: 'collaboration_task' as const,
            priority: 'medium' as const,
            status: t.task_status === 'complete' ? 'completed' as const : t.task_status === 'started' || t.task_status === 'in_progress' ? 'in_progress' as const : 'scheduled' as const,
            start_datetime: t.start_date,
            end_datetime: t.end_date,
            is_all_day: false,
            patient_name: t.patient_name,
            collaboration_id: t.collaboration_id,
            collaboration_title: t.collaboration_title,
            task_charge: t.task_charge,
        }))
    ];

    /* ─── Filter ─── */
    const filteredEvents = mergedEvents.filter(ev => {
        const typeOk = filter === 'all' || ev.event_type === filter;
        const searchOk = !searchQuery || ev.title.toLowerCase().includes(searchQuery.toLowerCase()) || (ev.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        return typeOk && searchOk;
    });

    const getEventsForDate = (date: Date) => {
        const ds = date.toISOString().split('T')[0];
        return filteredEvents.filter(ev => ev.start_datetime?.startsWith(ds));
    };

    /* ─── Timer management ─── */
    const startTimer = async (id: string) => { const h = await getAuthHeaders(); const r = await fetch(`${baseUrl}calendar/api/v1/timers/${id}/start/`, { method: 'POST', headers: h }); if (r.ok) { setActiveTimers(p => new Set(p).add(id)); loadData(); } };
    const pauseTimer = async (id: string) => { const h = await getAuthHeaders(); const r = await fetch(`${baseUrl}calendar/api/v1/timers/${id}/pause/`, { method: 'POST', headers: h }); if (r.ok) { setActiveTimers(p => { const s = new Set(p); s.delete(id); return s; }); loadData(); } };
    const completeTimer = async (id: string) => { const h = await getAuthHeaders(); const r = await fetch(`${baseUrl}calendar/api/v1/timers/${id}/complete/`, { method: 'POST', headers: h }); if (r.ok) { setActiveTimers(p => { const s = new Set(p); s.delete(id); return s; }); loadData(); } };

    /* ─── Context menu ─── */
    const handleContextMenu = (e: React.MouseEvent, date?: Date) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ show: true, x: e.clientX, y: e.clientY, date }); };
    const closeContextMenu = () => setContextMenu({ show: false, x: 0, y: 0 });
    const handleContextCreateEvent = (date?: Date) => { if (date) setCurrentDate(date); setEventModalMode('create'); setShowEventModal(true); };
    const handleContextCreateTimer = () => { setTimerModalMode('create'); setShowTimerModal(true); };
    const handleContextViewDate = (date: Date) => { setCurrentDate(date); setView('day'); };
    const handleContextCopyDate = (date: Date) => { navigator.clipboard.writeText(date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })); };
    const handleContextExportCalendar = () => {};
    const handleContextPrintCalendar = () => window.print();
    const handleContextShareCalendar = () => {};
    const handleContextSetReminder = (date: Date) => {};

    /* ─── Event CRUD ─── */
    const handleCreateEvent = async (eventData: any) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${baseUrl}calendar/api/v1/events/`, { method: 'POST', headers, body: JSON.stringify(eventData) });
            if (response.ok) { loadData(); setShowEventModal(false); }
            else { const t = await response.text(); alert(`Failed: ${response.status} - ${t}`); }
        } catch (error: any) { alert(`Error: ${error?.message}`); }
    };

    const handleCreateTimer = async (timerData: any) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${baseUrl}calendar/api/v1/timers/`, { method: 'POST', headers, body: JSON.stringify(timerData) });
            if (response.ok) { loadData(); setShowTimerModal(false); }
        } catch (error) { console.error('Error creating timer:', error); }
    };

    const handleEventClick = (event: CalendarEvent) => { setSelectedEvent(event); setEventModalMode('view'); setShowEventModal(true); };
    const handleCreateEventClick = () => { setSelectedEvent(null); setEventModalMode('create'); setShowEventModal(true); };
    const handleCreateTimerClick = () => { setTimerModalMode('create'); setShowTimerModal(true); };

    /* ─── Helpers ─── */
    const palette = (type: string) => TYPE_PALETTE[type] || TYPE_PALETTE.task;
    const formatTime = (ds: string) => new Date(ds).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const formatDateLong = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    const isToday = (d: Date) => isSameDay(d, new Date());

    /* ─── Navigation ─── */
    const navigateDate = (dir: 'prev' | 'next') => {
        const d = new Date(currentDate);
        if (view === 'month') d.setMonth(d.getMonth() + (dir === 'next' ? 1 : -1));
        else if (view === 'week') d.setDate(d.getDate() + (dir === 'next' ? 7 : -7));
        else d.setDate(d.getDate() + (dir === 'next' ? 1 : -1));
        setCurrentDate(d);
    };

    const getEventPosition = (s: string) => { const d = new Date(s); return ((d.getHours() - 6) * 60 + d.getMinutes()) / (16 * 60) * 100; };
    const getEventHeight = (s: string, e: string) => ((new Date(e).getTime() - new Date(s).getTime()) / 60000) / (16 * 60) * 100;

    const handleDayClick = (date: Date) => {
        const dayEvs = getEventsForDate(date);
        if (dayEvs.length > 0) { setSelectedDayEvents(dayEvs); setShowDayPopup(true); }
        else { setCurrentDate(date); setView('day'); }
    };

    /* ─── Today summary ─── */
    const todayEvents = getEventsForDate(new Date());
    const upcomingEvents = filteredEvents
        .filter(ev => new Date(ev.start_datetime) > new Date())
        .sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime())
        .slice(0, 5);

    /* ─── Mini Calendar ─── */
    const renderMiniCalendar = () => {
        const y = sidebarMiniDate.getFullYear();
        const m = sidebarMiniDate.getMonth();
        const first = new Date(y, m, 1);
        const startDate = new Date(first);
        startDate.setDate(startDate.getDate() - first.getDay());
        const cells = [];
        const cur = new Date(startDate);
        for (let i = 0; i < 42; i++) {
            const d = new Date(cur);
            const evCount = getEventsForDate(d).length;
            const isCurMonth = d.getMonth() === m;
            const today = isToday(d);
            const isSelected = isSameDay(d, currentDate);
            cells.push(
                <div key={i} onClick={() => { setCurrentDate(d); setView('day'); }}
                    className={`mini-day${!isCurMonth ? ' text-muted opacity-50' : ''}${today ? ' mini-today' : ''}${isSelected ? ' mini-selected' : ''}`}
                    style={{ cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: today || isSelected ? 700 : 400, background: isSelected ? ACCENT : today ? ACCENT_SOFT : 'transparent', color: isSelected ? '#fff' : today ? ACCENT : undefined, position: 'relative' }}>
                    {d.getDate()}
                    {evCount > 0 && <span style={{ position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: isSelected ? '#fff' : ACCENT }}></span>}
                </div>
            );
            cur.setDate(cur.getDate() + 1);
        }
        return (
            <div className="sc-card" style={{ margin: '0 0 12px 0' }}>
                <div className="sc-card-body" style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" style={{ padding: '2px 6px' }} onClick={() => setSidebarMiniDate(new Date(y, m - 1, 1))}><i className="bx bx-chevron-left"></i></button>
                        <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>{sidebarMiniDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" style={{ padding: '2px 6px' }} onClick={() => setSidebarMiniDate(new Date(y, m + 1, 1))}><i className="bx bx-chevron-right"></i></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, textAlign: 'center' }}>
                        {['S','M','T','W','T','F','S'].map((d, i) => <div key={i} className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 0' }}>{d}</div>)}
                        {cells}
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Month view ─── */
    const renderMonthView = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const first = new Date(year, month, 1);
        const start = new Date(first);
        start.setDate(start.getDate() - first.getDay());
        const days = [];
        const cur = new Date(start);

        for (let i = 0; i < 42; i++) {
            const d = new Date(cur);
            const dayEv = getEventsForDate(d);
            const isCurMonth = d.getMonth() === month;
            const today = isToday(d);

            days.push(
                <div key={i}
                    className={`cal-cell${!isCurMonth ? ' cal-other' : ''}${today ? ' cal-today' : ''}`}
                    onClick={() => handleDayClick(d)}
                    onContextMenu={(e) => handleContextMenu(e, d)}
                >
                    <div className={`cal-num${today ? ' cal-num-today' : ''}`}>{d.getDate()}</div>
                    <div className="cal-dots">
                        {dayEv.slice(0, 3).map((ev, idx) => (
                            <div key={idx} className="cal-ev-pill" style={{ background: palette(ev.event_type).bg }}
                                onClick={(e) => { e.stopPropagation(); handleEventClick(ev); }}>
                                <span className="cal-ev-text">{ev.title}</span>
                            </div>
                        ))}
                        {dayEv.length > 3 && <div className="cal-more">+{dayEv.length - 3} more</div>}
                    </div>
                </div>
            );
            cur.setDate(cur.getDate() + 1);
        }

        return (
            <div className="cal-grid">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="cal-hdr">{d}</div>)}
                {days}
            </div>
        );
    };

    /* ─── Week view ─── */
    const renderWeekView = () => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

        return (
            <div className="wk-container">
                <div className="wk-header">
                    <div className="wk-time-gutter"></div>
                    {Array.from({ length: 7 }).map((_, i) => {
                        const d = new Date(startOfWeek);
                        d.setDate(startOfWeek.getDate() + i);
                        const today = isToday(d);
                        return (
                            <div key={i} className={`wk-day-header${today ? ' wk-today-hdr' : ''}`}>
                                <div className="wk-day-name">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                <div className={`wk-day-num${today ? ' wk-today-num' : ''}`}>{d.getDate()}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="wk-body" style={{ maxHeight: 600, overflowY: 'auto' }}>
                    <div className="wk-time-gutter">
                        {Array.from({ length: 17 }).map((_, h) => (
                            <div key={h} className="wk-time-slot"><span className="wk-time-label">{h + 6 === 12 ? '12 PM' : h + 6 > 12 ? `${h + 6 - 12} PM` : `${h + 6} AM`}</span></div>
                        ))}
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => {
                        const d = new Date(startOfWeek);
                        d.setDate(startOfWeek.getDate() + i);
                        const dayEv = getEventsForDate(d);
                        return (
                            <div key={i} className={`wk-day-col${isToday(d) ? ' wk-today-col' : ''}`} onContextMenu={(e) => handleContextMenu(e, d)}>
                                {Array.from({ length: 17 }).map((_, h) => <div key={h} className="wk-slot-cell"></div>)}
                                {dayEv.map(ev => (
                                    <div key={ev.id} className="wk-event"
                                        style={{ top: `${getEventPosition(ev.start_datetime)}%`, height: `${Math.max(getEventHeight(ev.start_datetime, ev.end_datetime), 3)}%`, background: palette(ev.event_type).bg, color: palette(ev.event_type).text }}
                                        onClick={() => handleEventClick(ev)}>
                                        <div style={{ fontSize: '0.65rem', fontWeight: 600 }}>{formatTime(ev.start_datetime)}</div>
                                        <div style={{ fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    /* ─── Day view ─── */
    const renderDayView = () => {
        const dayEv = getEventsForDate(currentDate);
        return (
            <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h5 className="mb-0 fw-bold">{formatDateLong(currentDate)}</h5>
                        <small className="text-muted">{dayEv.length} event{dayEv.length !== 1 ? 's' : ''} scheduled</small>
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={handleCreateEventClick}><i className="bx bx-plus me-1"></i>Add Event</button>
                </div>
                <div style={{ position: 'relative', minHeight: 680 }}>
                    <div className="d-flex">
                        <div style={{ width: 56, flexShrink: 0 }}>
                            {Array.from({ length: 17 }).map((_, h) => (
                                <div key={h} style={{ height: 40, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.7rem', color: '#999', fontWeight: 500 }}>{h + 6 === 12 ? '12 PM' : h + 6 > 12 ? `${h + 6 - 12} PM` : `${h + 6} AM`}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ flex: 1, position: 'relative' }}>
                            {Array.from({ length: 17 }).map((_, h) => <div key={h} style={{ height: 40, borderBottom: '1px solid #f5f5f5' }}></div>)}
                            {dayEv.map(ev => (
                                <motion.div key={ev.id} whileHover={{ scale: 1.01, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                                    style={{ position: 'absolute', left: 4, right: 4, top: `${getEventPosition(ev.start_datetime) / 100 * 680}px`, height: Math.max(getEventHeight(ev.start_datetime, ev.end_datetime) / 100 * 680, 30), background: '#fff', borderLeft: `4px solid ${palette(ev.event_type).dot}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', zIndex: 2 }}
                                    onClick={() => handleEventClick(ev)}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: palette(ev.event_type).dot }}>{formatTime(ev.start_datetime)} – {formatTime(ev.end_datetime)}</span>
                                        <span className={`badge bg-${PRIORITY_COLORS[ev.priority] || 'primary'}-subtle text-${PRIORITY_COLORS[ev.priority] || 'primary'}`} style={{ fontSize: '0.65rem' }}>{ev.priority}</span>
                                    </div>
                                    <div className="fw-semibold mt-1" style={{ fontSize: '0.85rem' }}>{ev.title}</div>
                                    {ev.location && <div className="text-muted" style={{ fontSize: '0.72rem' }}><i className="bx bx-map me-1"></i>{ev.location}</div>}
                                    {ev.collaboration_title && <div style={{ fontSize: '0.7rem' }}><span className="badge bg-info-subtle text-info">Collab: {ev.collaboration_title}</span></div>}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Agenda view ─── */
    const renderAgendaView = () => {
        const sorted = [...filteredEvents].sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime());
        const grouped: Record<string, CalendarEvent[]> = {};
        sorted.forEach(ev => {
            const key = new Date(ev.start_datetime).toDateString();
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(ev);
        });

        return (
            <div style={{ maxHeight: 700, overflowY: 'auto' }}>
                {Object.entries(grouped).map(([dateKey, evs]) => (
                    <div key={dateKey} className="mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className={`badge ${isToday(new Date(dateKey)) ? 'bg-primary' : 'bg-light text-dark'} px-3 py-2`} style={{ fontSize: '0.8rem' }}>
                                {isToday(new Date(dateKey)) ? 'Today' : new Date(dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            <div style={{ flex: 1, height: 1, background: '#e9ecef' }}></div>
                        </div>
                        {evs.map(ev => (
                            <motion.div key={ev.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                className="d-flex align-items-center gap-3 p-3 mb-2 rounded-3" style={{ background: '#fff', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'all 0.15s' }}
                                whileHover={{ x: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
                                onClick={() => handleEventClick(ev)}>
                                <div style={{ width: 4, height: 40, borderRadius: 4, background: palette(ev.event_type).dot, flexShrink: 0 }}></div>
                                <div style={{ minWidth: 60 }}>
                                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057' }}>{formatTime(ev.start_datetime)}</div>
                                    <div style={{ fontSize: '0.68rem', color: '#999' }}>{formatTime(ev.end_datetime)}</div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div className="fw-semibold" style={{ fontSize: '0.88rem' }}>{ev.title}</div>
                                    <div className="d-flex gap-1 mt-1 flex-wrap">
                                        <span className="badge" style={{ background: palette(ev.event_type).bg, color: palette(ev.event_type).text, fontSize: '0.63rem' }}>{palette(ev.event_type).label}</span>
                                        {ev.patient_name && <span className="badge bg-light text-dark" style={{ fontSize: '0.63rem' }}><i className="bx bx-user me-1"></i>{ev.patient_name}</span>}
                                        {ev.location && <span className="badge bg-light text-dark" style={{ fontSize: '0.63rem' }}><i className="bx bx-map me-1"></i>{ev.location}</span>}
                                    </div>
                                </div>
                                <i className="bx bx-chevron-right text-muted"></i>
                            </motion.div>
                        ))}
                    </div>
                ))}
                {Object.keys(grouped).length === 0 && (
                    <div className="text-center py-5">
                        <i className="bx bx-calendar-x display-4 text-muted"></i>
                        <p className="mt-2 text-muted">No events match your filters</p>
                    </div>
                )}
            </div>
        );
    };

    /* ─── RENDER ─── */
    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* ── Page Header ── */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-calendar-event me-2" style={{ color: ACCENT }}></i>Calendar & Scheduling</h1>
                                <p>Manage events, appointments, and collaboration tasks</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadData}><i className="bx bx-refresh"></i> Refresh</button>
                                <button className="sc-btn sc-btn-ghost" onClick={handleCreateTimerClick}><i className="bx bx-timer"></i> Timer</button>
                                <button className="sc-btn sc-btn-primary" onClick={handleCreateEventClick}><i className="bx bx-plus"></i> New Event</button>
                            </div>
                        </div>

                        {/* ── Quick Stats Row ── */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Events', val: stats?.total_events || filteredEvents.length, icon: 'bx bx-calendar', cls: 'teal' },
                                { label: 'Today', val: todayEvents.length, icon: 'bx bx-calendar-check', cls: 'blue' },
                                { label: 'Upcoming', val: stats?.upcoming_events || upcomingEvents.length, icon: 'bx bx-time-five', cls: 'amber' },
                                { label: 'Overdue', val: stats?.overdue_events || 0, icon: 'bx bx-error', cls: 'red' },
                                { label: 'Collab Tasks', val: collabTasks.length, icon: 'bx bx-task', cls: 'violet' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* ── Toolbar ── */}
                        <div className="sc-card" style={{ marginBottom: 16 }}>
                            <div className="sc-card-body" style={{ padding: '12px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {/* View Switcher */}
                                    <div style={{ display: 'flex', gap: 2, background: 'rgba(15,23,42,0.03)', padding: 3, borderRadius: 10 }}>
                                        {(['month', 'week', 'day', 'agenda'] as const).map(v => (
                                            <button key={v} onClick={() => setView(v)}
                                                style={{ padding: '5px 14px', borderRadius: 8, border: 'none', background: view === v ? '#fff' : 'transparent', color: view === v ? ACCENT : 'var(--uzima-shell-text-muted)', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', boxShadow: view === v ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                                                <i className={`bx bx-${v === 'month' ? 'calendar' : v === 'week' ? 'grid-alt' : v === 'day' ? 'calendar-event' : 'list-ul'} me-1`}></i>
                                                {v.charAt(0).toUpperCase() + v.slice(1)}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Navigation */}
                                    <div style={{ display: 'flex', gap: 2 }}>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigateDate('prev')}><i className="bx bx-chevron-left"></i></button>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setCurrentDate(new Date())}>Today</button>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigateDate('next')}><i className="bx bx-chevron-right"></i></button>
                                    </div>

                                    <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div className="sc-search" style={{ marginBottom: 0, maxWidth: 200 }}>
                                        <i className="bx bx-search"></i>
                                        <input type="text" placeholder="Search events…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                    </div>
                                    <select className="sc-input" style={{ width: 140, padding: '6px 10px', fontSize: '0.76rem' }}
                                        value={filter} onChange={(e) => setFilter(e.target.value)}>
                                        <option value="all">All Types</option>
                                        {Object.entries(TYPE_PALETTE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* ── Main Content ── */}
                        <div className="row">
                            {/* Calendar */}
                            <div className="col-xl-9 col-lg-8">
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div className="sc-card-body" style={{ padding: 12 }}>
                                        {loading ? (
                                            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                                <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: ACCENT }}></i>
                                                <p style={{ marginTop: 12, color: 'var(--uzima-shell-text-muted)' }}>Loading calendar…</p>
                                            </div>
                                        ) : (
                                            <AnimatePresence mode="wait">
                                                <motion.div key={view} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                                                    {view === 'month' && renderMonthView()}
                                                    {view === 'week' && renderWeekView()}
                                                    {view === 'day' && renderDayView()}
                                                    {view === 'agenda' && renderAgendaView()}
                                                </motion.div>
                                            </AnimatePresence>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="col-xl-3 col-lg-4">
                                {/* Mini Calendar */}
                                {renderMiniCalendar()}

                                {/* Today At a Glance */}
                                <div className="sc-card" style={{ margin: '0 0 12px 0' }}>
                                    <div className="sc-card-body" style={{ padding: 14 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 10 }}><i className="bx bx-sun me-1" style={{ color: '#d97706' }}></i>Today</div>
                                        {todayEvents.length === 0 ? (
                                            <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 0 }}><i className="bx bx-check-circle me-1" style={{ color: ACCENT }}></i>No events today — enjoy your day!</p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {todayEvents.slice(0, 4).map(ev => (
                                                    <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, background: 'rgba(15,23,42,0.02)', cursor: 'pointer' }} onClick={() => handleEventClick(ev)}>
                                                        <div style={{ width: 3, height: 28, borderRadius: 2, background: palette(ev.event_type).dot, flexShrink: 0 }}></div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)' }}>{formatTime(ev.start_datetime)}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {todayEvents.length > 4 && <span style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>+{todayEvents.length - 4} more</span>}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Upcoming */}
                                <div className="sc-card" style={{ margin: '0 0 12px 0' }}>
                                    <div className="sc-card-body" style={{ padding: 14 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 10 }}><i className="bx bx-time-five me-1" style={{ color: '#3b82f6' }}></i>Upcoming</div>
                                        {upcomingEvents.length === 0 ? (
                                            <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 0 }}>No upcoming events</p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {upcomingEvents.map(ev => (
                                                    <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', borderRadius: 8, background: 'rgba(15,23,42,0.02)', cursor: 'pointer' }} onClick={() => handleEventClick(ev)}>
                                                        <div style={{ width: 3, height: 28, borderRadius: 2, background: palette(ev.event_type).dot, flexShrink: 0, marginTop: 2 }}></div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                                {new Date(ev.start_datetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {formatTime(ev.start_datetime)}
                                                            </div>
                                                        </div>
                                                        <span style={{ padding: '2px 8px', borderRadius: 12, background: palette(ev.event_type).bg, color: palette(ev.event_type).text, fontSize: '0.58rem', fontWeight: 700 }}>{palette(ev.event_type).label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Active Timers */}
                                {timers.filter(t => t.is_active).length > 0 && (
                                    <div className="sc-card" style={{ margin: '0 0 12px 0' }}>
                                        <div className="sc-card-body" style={{ padding: 14 }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 10 }}><i className="bx bx-timer me-1" style={{ color: ACCENT }}></i>Active Timers</div>
                                            {timers.filter(t => t.is_active).map(timer => (
                                                <div key={timer.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(15,23,42,0.02)', marginBottom: 6 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                                        <span style={{ fontWeight: 600, fontSize: '0.78rem' }}>{timer.title}</span>
                                                        <span style={{ padding: '2px 8px', borderRadius: 12, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.63rem', fontWeight: 700 }}>{timer.timer_type}</span>
                                                    </div>
                                                    <div style={{ height: 4, borderRadius: 2, background: '#e5e7eb', marginBottom: 4 }}>
                                                        <div style={{ height: '100%', borderRadius: 2, background: ACCENT, width: `${timer.progress_percentage || 0}%`, transition: 'width 0.3s' }}></div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <span style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)' }}>{timer.remaining_time || 0}m left</span>
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            <button className="sc-btn sc-btn-ghost" style={{ padding: '2px 6px', fontSize: '0.72rem' }} onClick={() => pauseTimer(timer.id)}><i className="bx bx-pause"></i></button>
                                                            <button className="sc-btn sc-btn-ghost" style={{ padding: '2px 6px', fontSize: '0.72rem', color: ACCENT }} onClick={() => completeTimer(timer.id)}><i className="bx bx-check"></i></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Event Type Legend */}
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div className="sc-card-body" style={{ padding: 14 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 10 }}><i className="bx bx-palette me-1"></i>Legend</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {Object.entries(TYPE_PALETTE).map(([k, v]) => (
                                                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} onClick={() => setFilter(filter === k ? 'all' : k)}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: v.dot, opacity: filter !== 'all' && filter !== k ? 0.3 : 1 }}></div>
                                                    <span style={{ fontSize: '0.72rem', opacity: filter !== 'all' && filter !== k ? 0.4 : 1 }}>{v.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DashboardFooter />

            {/* Day popup */}
            {showDayPopup && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => setShowDayPopup(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header border-0 pb-0">
                                <h6 className="modal-title fw-bold">Events for {selectedDayEvents[0] && new Date(selectedDayEvents[0].start_datetime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h6>
                                <button className="btn-close" onClick={() => setShowDayPopup(false)}></button>
                            </div>
                            <div className="modal-body pt-2">
                                {selectedDayEvents.map(ev => (
                                    <div key={ev.id} className="d-flex align-items-center gap-3 p-3 mb-2 rounded-3" style={{ background: '#f8f9fa', cursor: 'pointer' }}
                                        onClick={() => { setShowDayPopup(false); handleEventClick(ev); }}>
                                        <div style={{ width: 4, height: 36, borderRadius: 4, background: palette(ev.event_type).dot }}></div>
                                        <div style={{ flex: 1 }}>
                                            <div className="fw-semibold">{ev.title}</div>
                                            <div className="text-muted small">{formatTime(ev.start_datetime)} – {formatTime(ev.end_datetime)}</div>
                                        </div>
                                        <span className="badge" style={{ background: palette(ev.event_type).bg, color: palette(ev.event_type).text, fontSize: '0.65rem' }}>{palette(ev.event_type).label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Context Menu */}
            <CalendarContextMenu show={contextMenu.show} x={contextMenu.x} y={contextMenu.y} date={contextMenu.date} view={view}
                onClose={closeContextMenu} onCreateEvent={handleContextCreateEvent} onCreateTimer={handleContextCreateTimer}
                onViewDate={handleContextViewDate} onCopyDate={handleContextCopyDate} onExportCalendar={handleContextExportCalendar}
                onPrintCalendar={handleContextPrintCalendar} onShareCalendar={handleContextShareCalendar} onSetReminder={handleContextSetReminder} />

            {/* Event Modal */}
            <EventModal show={showEventModal} onHide={() => setShowEventModal(false)} onSubmit={handleCreateEvent} event={selectedEvent} mode={eventModalMode} />

            {/* Timer Modal */}
            <TimerModal show={showTimerModal} onHide={() => setShowTimerModal(false)} onSubmit={handleCreateTimer} timer={null} mode={timerModalMode} />
        </>
    );
};

export default ModernCalendar;
